import type { BadgeColor } from '@enamelai/design-system'
import { getEnv } from '@utils/env'

/** Per-file backup status column; the files API doesn't return a status yet. */
export const CLOUD_STORAGE_STATUS_ENABLED =
  getEnv('REACT_APP_CLOUD_STORAGE_STATUS_ENABLED', 'false') === 'true'

/** Row actions column; no file actions are wired up yet. */
export const CLOUD_STORAGE_ACTIONS_ENABLED =
  getEnv('REACT_APP_CLOUD_STORAGE_ACTIONS_ENABLED', 'false') === 'true'

/** Header "Edit info" button; there is no plan edit flow yet. */
export const CLOUD_STORAGE_EDIT_INFO_ENABLED =
  getEnv('REACT_APP_CLOUD_STORAGE_EDIT_INFO_ENABLED', 'false') === 'true'

/** Monthly price and the time-to-capacity projection on the plan card. */
export const CLOUD_STORAGE_PLAN_DETAILS_ENABLED =
  getEnv('REACT_APP_CLOUD_STORAGE_PLAN_DETAILS_ENABLED', 'false') === 'true'

export type StorageCategoryId =
  | 'imaging'
  | 'forms'
  | 'backup'
  | 'auditLogs'
  | 'other'

export interface StorageCategory {
  id: StorageCategoryId
  label: string
  usedGb: number
  usedBytes: number
  colorVar: string
  legendColorVar: string
}

/**
 * Originating module of a stored file. `imaging` covers every X-ray upload path
 * (Files, X-ray viewer, OHIF, charting) — those share one endpoint and folder,
 * so they can't be told apart from stored data.
 */
export type StorageSourceId =
  | 'imaging'
  | 'dicomIngest'
  | 'claims'
  | 'patientPhoto'
  | 'providerPhoto'
  | 'files'

/** Outcome of the upload / backup run. Backup vs. manual backup is shown in Source. */
export type CloudStorageFileStatus = 'success' | 'failed'

export type CloudStorageFile = {
  id: string
  fileName: string
  type: string
  categoryId: StorageCategoryId
  /** Display label of the storage category (Imaging / Forms / Other). */
  categoryLabel: string
  patient: string
  fileSize: string
  /** Raw size for sorting; `fileSize` is the formatted display string. */
  fileSizeBytes: number
  /** Module the upload came from, resolved server-side. */
  source: StorageSourceId
  sourceLabel: string
  /** Owning folder name, or a placeholder when the file has no folder. */
  location: string
  uploadedBy: string
  uploadedAt: string
  /** Epoch ms for sorting; `null` when the upload time is unknown. */
  uploadedAtTime: number | null
  status?: CloudStorageFileStatus
}

/** A single row of the per-clinic storage breakdown. */
export interface StorageClinicUsage {
  clinicId: string | null
  clinicName: string
  usedGb: number
  usedBytes: number
  fileCount: number
  /** Share of the tenant-wide used storage, 0–100. */
  usedPercent: number
}

export interface CloudStoragePlan {
  name: string
  renewsOn: string
  monthlyPrice: number
  usedGb: number
  totalGb: number
  /** Raw byte totals so sub-GB usage renders as KB/MB instead of "0 GB". */
  usedBytes: number
  totalBytes: number
  usedPercent: number
  growthRateBytesPerMonth: number
  monthsUntilCapacity: number | null
}

/**
 * Category color/label/order metadata. `usedGb` is a placeholder (`0`) — the
 * live per-category totals are merged in by the API layer (`toCategories`).
 */
export const CLOUD_STORAGE_CATEGORIES: StorageCategory[] = [
  {
    id: 'backup',
    label: 'Backup',
    usedGb: 0,
    usedBytes: 0,
    colorVar: '--ds-blue-500',
    legendColorVar: '--ds-blue-500',
  },
  {
    id: 'forms',
    label: 'Forms',
    usedGb: 0,
    usedBytes: 0,
    colorVar: '--ds-lime-green-300',
    legendColorVar: '--ds-lime-green-300',
  },
  {
    id: 'imaging',
    label: 'Imaging',
    usedGb: 0,
    usedBytes: 0,
    colorVar: '--ds-cyan-400',
    legendColorVar: '--ds-cyan-400',
  },
  {
    id: 'auditLogs',
    label: 'Audit',
    usedGb: 0,
    usedBytes: 0,
    colorVar: '--ds-yellow-300',
    legendColorVar: '--ds-yellow-300',
  },
  {
    id: 'other',
    label: 'Other',
    usedGb: 0,
    usedBytes: 0,
    colorVar: '--ds-gray-200',
    legendColorVar: '--ds-gray-200',
  },
]

export const CLOUD_STORAGE_SOURCE_BADGE_COLORS: Record<
  StorageSourceId,
  BadgeColor
> = {
  imaging: 'blue',
  dicomIngest: 'indigo',
  claims: 'orange',
  patientPhoto: 'vivid-green',
  providerPhoto: 'vivid-green',
  files: 'gray',
}

export const CLOUD_STORAGE_STATUS_BADGES: Record<
  CloudStorageFileStatus,
  { label: string; color: BadgeColor }
> = {
  success: { label: 'Success', color: 'vivid-green' },
  failed: { label: 'Failed', color: 'red' },
}

export const CLOUD_STORAGE_FILTER_LABELS: Record<string, string> = {
  category: 'Category',
  type: 'File type',
  source: 'Source',
}

/** Shown when a file has no owning folder (patient/provider profile photos). */
export const NO_FOLDER_LABEL = '—'

/** Shown when a file isn't linked to a patient (provider photos, tenant uploads). */
export const NO_PATIENT_LABEL = '—'
